import { Schema } from '@cfworker/json-schema';
import {
  ApiLpErrorResolutionPolicy, IApiLp, IApiLpDetails, IApiLpErrorResolution, IApiLpUpdate, IApiLpConnectionSettings,
} from '@cbridge-packages/api';
import { LpTranslator } from '@cbridge-packages/lp';
import { ILpAdapterSchemas, getReadOnlyParameterKeys, isInboundAdapter, isTradingDisabled } from '@cbridge-packages/lp-adapters';
import { ScheduleTranslator } from '@cbridge-packages/schedule';
import { IClientLp, IClientLpErrorResolution, IClientLpForm, IConnectionSettings } from './clientLp.interface';

export namespace ClientLpTranslator {
  function fromApiErrorResolution(errorResolution?: IApiLpErrorResolution): IClientLpErrorResolution {
    return {
      policy: errorResolution?.policy ?? ApiLpErrorResolutionPolicy.Manual,
      resetSeqNumFlagSupported: errorResolution?.resetSeqNumFlagSupported ?? false,
    };
  }

  export function fromApi(lp: IApiLp): IClientLp {
    return {
      lpId: lp.id,
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      errorResolution: fromApiErrorResolution(lp.errorResolution),
      schedule: lp.schedule && {
        scheduleId: lp.schedule?.id,
        scheduleName: lp.schedule?.name,
        timezone: undefined,
        pricingIntervals: undefined,
        tradingIntervals: undefined,
      },
      trading: lp.trading ? LpTranslator.fromApiLpConnectionState(lp.trading) : undefined,
      pricing: LpTranslator.fromApiLpConnectionState(lp.pricing),
    };
  }

  export function fromApiDetails(lp: IApiLpDetails): IClientLp {
    const schedule = lp.schedule ? ScheduleTranslator.fromApiFullView(lp.schedule) : undefined;
    return {
      lpId: lp.id,
      name: lp.name,
      region: lp.region,
      adapter: lp.adapter,
      enabled: lp.enabled,
      errorResolution: fromApiErrorResolution(lp.errorResolution),
      schedule,
      trading: lp.trading ? LpTranslator.fromApiConnection(lp.trading) : undefined,
      pricing: LpTranslator.fromApiConnection(lp.pricing),
    };
  }

  interface IToApiConnectionSettingsOptions {
    // Inbound sessions are opened by the LP with CP-issued credentials: login/password are not updatable.
    omitCredentials?: boolean;
    // Parameters CP marks `readOnly` are never sent back.
    schema?: Schema | null;
  }

  export function toApiConnectionSettings(
    connection: Partial<IConnectionSettings> = {},
    options: IToApiConnectionSettingsOptions = {},
  ): IApiLpConnectionSettings {
    const readOnlyKeys = getReadOnlyParameterKeys(options.schema);
    const adapterSettings = (connection.adapterSettings ?? [])
      .filter(({ key, value }) => key?.trim() && value?.trim() && !readOnlyKeys.has(key))
      .map(({ key, value }) => ({ key, value }));

    return options.omitCredentials
      ? { adapterSettings }
      : { login: connection.login, password: connection.password, adapterSettings };
  }

  // `schemas` — the adapter schemas of the LP (undefined while not loaded): they decide
  // whether the trading block and the read-only parameters go to the API.
  export function toApi(lp: IClientLpForm, schemas?: ILpAdapterSchemas | null): IApiLpUpdate {
    const hasTrading = lp.trading !== undefined && !isTradingDisabled(schemas);

    return {
      lpId: lp.lpId,
      name: lp.name,
      enabled: lp.enabled,
      trading: hasTrading ? ClientLpTranslator.toApiConnectionSettings(lp.trading, { schema: schemas?.trading }) : undefined,
      pricing: ClientLpTranslator.toApiConnectionSettings(lp.pricing, {
        omitCredentials: isInboundAdapter(schemas),
        schema: schemas?.pricing,
      }),
      scheduleId: lp.scheduleId ?? null,
    };
  }
}
